import { useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { L } from '../ui/LavDropdown'
import { SvIc, ServiceFormFields, svFormValid } from './svBits'

/**
 * Add/Edit + Manage-categories surfaces for the Services feature, from the
 * Claude Design "Services.html" spec: SvDrawer (desktop right drawer),
 * SvmFormScreen (mobile full-screen form), SvManageDialog (desktop modal) and
 * SvmCatsScreen (mobile full-screen categories manager). All presentational —
 * persistence comes in through callbacks from ServicesManager.
 *
 * The add/edit form is *controlled*: `form`/`setField` are owned by
 * ServicesManager so the categories manager can be opened on top of (mobile:
 * in place of) the form without discarding what the user has typed.
 */

const DANGER = '#BE4B45'
const DANGER_DEEP = '#A53B36'
const DANGER_SOFT = '#FBECEB'

const xBtnSx = {
	width: 36, height: 36, flex: 'none', border: `1.5px solid ${L.border}`,
	borderRadius: '10px', bgcolor: '#FFFFFF', color: L.muted, display: 'flex',
	alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit',
	'&:hover': { color: L.text, borderColor: L.muted },
}

const iconBtnSx = {
	width: 30, height: 30, flex: 'none', border: `1px solid ${L.border}`, borderRadius: '8px',
	bgcolor: '#FFFFFF', color: L.muted, display: 'flex', alignItems: 'center',
	justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit', p: 0,
	'&:hover:not(:disabled)': { color: L.primaryDeep, borderColor: L.primary },
	'&:disabled': { opacity: 0.4, cursor: 'default' },
}

const dangerIconBtnSx = {
	...iconBtnSx,
	'&:hover:not(:disabled)': { color: DANGER, borderColor: DANGER, bgcolor: DANGER_SOFT },
}

export const svGhostBtnSx = {
	display: 'inline-flex', alignItems: 'center', gap: '7px', height: 40, px: '14px',
	border: `1.5px solid ${L.border}`, borderRadius: '10px', bgcolor: '#FFFFFF', color: L.text,
	fontSize: '13px', fontWeight: 650, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
	'&:hover:not(:disabled)': { borderColor: L.primary, color: L.primaryDeep },
	'&:disabled': { color: L.muted, opacity: 0.6, cursor: 'default' },
}

export const svPrimaryBtnSx = {
	display: 'inline-flex', alignItems: 'center', gap: '7px', height: 40, px: '16px',
	border: 'none', borderRadius: '10px', bgcolor: L.primary, color: L.onPrimary,
	fontSize: '13.5px', fontWeight: 700, boxShadow: '0 2px 8px rgba(124,107,217,0.28)',
	whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
	'&:hover': { bgcolor: L.primaryDeep },
}

const catNameInputSx = {
	height: 32, border: `1.5px solid ${L.primary}`, borderRadius: '8px', bgcolor: '#FFFFFF',
	p: '0 10px', fontFamily: 'inherit', fontSize: '14px', fontWeight: 700, color: L.text,
	outline: 'none', minWidth: 0, flex: 1,
}

const plainInputSx = {
	width: '100%', height: 44, border: `1.5px solid ${L.border}`, borderRadius: '10px',
	bgcolor: '#FFFFFF', p: '0 13px', fontFamily: 'inherit', fontSize: '14px', color: L.text,
	outline: 'none',
	'&:focus': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
	'&::placeholder': { color: '#B9B2CC', fontWeight: 500 },
}

/* ── desktop add/edit drawer (sv-drawer) ── */
export function SvDrawer({ mode, service, form, setField, catsByType, currencySymbol, saving, onClose, onSave, onDelete, onManageCats }) {
	const [confirming, setConfirming] = useState(false)
	const edit = mode === 'edit'
	const noun = form.type === 'addon' ? 'add-on' : 'service'

	return (
		<Box
			onClick={onClose}
			sx={{
				position: 'fixed', inset: 0, zIndex: 1300, bgcolor: 'rgba(46,42,64,0.34)',
				display: 'flex', justifyContent: 'flex-end', fontFamily: L.font, color: L.text,
			}}
		>
			<Box
				component="aside"
				role="dialog"
				aria-label={(edit ? 'Edit ' : 'New ') + noun}
				onClick={(e) => e.stopPropagation()}
				sx={{
					width: 408, maxWidth: '92%', height: '100%', bgcolor: '#FFFFFF',
					display: 'flex', flexDirection: 'column',
					boxShadow: '-10px 0 32px rgba(46,42,64,0.18)', fontSize: '14px',
				}}
			>
				<Box sx={{
					flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
					gap: '12px', p: '18px 20px 12px',
				}}>
					<Box component="h2" sx={{ m: 0, fontSize: '20px', fontWeight: 800, letterSpacing: '-0.015em' }}>
						{(edit ? 'Edit ' : 'New ') + noun}
					</Box>
					<Box component="button" type="button" aria-label="Close" onClick={onClose} sx={xBtnSx}>
						<SvIc name="x" size={15} sw={1.8} />
					</Box>
				</Box>
				<Box sx={{
					flex: 1, minHeight: 0, overflowY: 'auto', p: '6px 20px 18px',
					display: 'flex', flexDirection: 'column', gap: '14px',
				}}>
					<ServiceFormFields
						form={form}
						setField={setField}
						catsByType={catsByType}
						currencySymbol={currencySymbol}
						onManageCats={onManageCats}
					/>
				</Box>
				<Box sx={{
					flex: 'none', display: 'flex', alignItems: 'center', gap: '10px',
					p: '14px 20px 18px', borderTop: '1px solid #EFEBF7', bgcolor: '#FFFFFF',
				}}>
					{edit && (
						confirming ? (
							<Box
								component="button"
								type="button"
								disabled={saving}
								onClick={() => onDelete(service.id)}
								sx={{
									display: 'inline-flex', alignItems: 'center', height: 46, px: '13px',
									flex: 'none', border: 'none', borderRadius: '9px', bgcolor: DANGER,
									color: '#FFFFFF', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer',
									fontFamily: 'inherit',
									'&:hover': { bgcolor: DANGER_DEEP },
									'&:disabled': { opacity: 0.6, cursor: 'default' },
								}}
							>
								Confirm delete
							</Box>
						) : (
							<Box
								component="button"
								type="button"
								onClick={() => setConfirming(true)}
								sx={{
									display: 'inline-flex', alignItems: 'center', gap: '7px', height: 46,
									px: '13px', flex: 'none', border: 'none', borderRadius: '10px',
									bgcolor: 'transparent', color: DANGER, fontSize: '13px', fontWeight: 650,
									cursor: 'pointer', fontFamily: 'inherit',
									'&:hover': { bgcolor: DANGER_SOFT },
								}}
							>
								<SvIc name="trash" size={14} />
								<span>Delete</span>
							</Box>
						)
					)}
					<Box
						component="button"
						type="button"
						disabled={!svFormValid(form) || saving}
						onClick={() => onSave(form, edit ? service.id : null)}
						sx={{
							flex: 1, height: 46, border: 'none', borderRadius: '11px', bgcolor: L.primary,
							color: L.onPrimary, fontSize: '14.5px', fontWeight: 700,
							boxShadow: '0 2px 8px rgba(124,107,217,0.28)', cursor: 'pointer', fontFamily: 'inherit',
							display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
							'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
							'&:disabled': {
								bgcolor: L.primarySoft, color: '#A99DDD', border: '1px solid #DCD3F2',
								boxShadow: 'none', cursor: 'default',
							},
						}}
					>
						{saving && <CircularProgress size={16} sx={{ color: 'inherit' }} />}
						<span>{edit ? 'Save changes' : 'Save'}</span>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

/* ── mobile add/edit screen (svm-form) ── */
export function SvmFormScreen({ mode, service, form, setField, catsByType, currencySymbol, saving, onBack, onSave, onDelete, onManageCats }) {
	const [confirming, setConfirming] = useState(false)
	const edit = mode === 'edit'
	const noun = form.type === 'addon' ? 'add-on' : 'service'

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: L.bg, color: L.text, fontFamily: L.font, fontSize: '14px',
		}}>
			<Box component="header" sx={{
				flex: 'none', display: 'flex', alignItems: 'center', gap: '11px',
				p: '12px 16px 10px', bgcolor: L.bg, borderBottom: `1px solid ${L.border}`,
			}}>
				<Box
					component="button"
					type="button"
					aria-label="Back to services"
					onClick={onBack}
					sx={{
						width: 40, height: 40, flex: 'none', ml: '-8px', border: 'none',
						borderRadius: '11px', bgcolor: 'transparent', color: L.text,
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						cursor: 'pointer', fontFamily: 'inherit',
						'&:active': { bgcolor: L.surface },
					}}
				>
					<SvIc name="back" size={17} sw={1.8} />
				</Box>
				<Box component="strong" sx={{ flex: 1, minWidth: 0, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.015em' }}>
					{(edit ? 'Edit ' : 'New ') + noun}
				</Box>
				<Box
					component="button"
					type="button"
					disabled={!svFormValid(form) || saving}
					onClick={() => onSave(form, edit ? service.id : null)}
					sx={{
						display: 'inline-flex', alignItems: 'center', gap: '6px', height: 38, px: '15px',
						border: 'none', borderRadius: '11px', bgcolor: L.primary, color: L.onPrimary,
						fontSize: '13.5px', fontWeight: 700, boxShadow: '0 2px 8px rgba(124,107,217,0.28)',
						cursor: 'pointer', fontFamily: 'inherit',
						'&:active': { bgcolor: L.primaryDeep },
						'&:disabled': { bgcolor: L.primarySoft, color: '#A99DDD', boxShadow: 'none', cursor: 'default' },
					}}
				>
					{saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <SvIc name="check" size={14} sw={2.2} />}
					<span>Save</span>
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '16px' }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
					<ServiceFormFields
						form={form}
						setField={setField}
						catsByType={catsByType}
						currencySymbol={currencySymbol}
						onManageCats={onManageCats}
						tall
					/>
					{edit && (
						<Box
							component="button"
							type="button"
							disabled={saving}
							onClick={() => (confirming ? onDelete(service.id) : setConfirming(true))}
							sx={{
								display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
								height: 46, mt: '6px',
								border: `1.5px solid ${confirming ? DANGER : '#F2D7D5'}`, borderRadius: '12px',
								bgcolor: confirming ? DANGER : '#FFFFFF', color: confirming ? '#FFFFFF' : DANGER,
								fontSize: '14px', fontWeight: 650, cursor: 'pointer', fontFamily: 'inherit',
								'&:active': { bgcolor: confirming ? DANGER_DEEP : DANGER_SOFT },
								'&:disabled': { opacity: 0.6, cursor: 'default' },
							}}
						>
							<SvIc name="trash" size={15} />
							<span>{confirming ? 'Tap again to confirm delete' : `Delete this ${noun}`}</span>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	)
}

/* ── manage categories dialog (desktop, sv-modal) ── */
export function SvManageDialog({ title, cats, countFor, saving, focusAdd, onClose, onReorder, onDragEnd, onRename, onAdd, onDelete }) {
	const [renameId, setRenameId] = useState(null)
	const [newName, setNewName] = useState('')
	const [dragId, setDragId] = useState(null)
	const commit = (id, v) => {
		setRenameId(null)
		const name = v.trim()
		if (name) onRename(id, name)
	}
	const add = () => {
		const name = newName.trim()
		if (name) {
			onAdd(name)
			setNewName('')
		}
	}

	return (
		<Box
			onClick={onClose}
			sx={{
				// Above SvDrawer (1300) — this dialog can be opened from inside the
				// add/edit drawer via the category dropdown's "New category…" row.
				position: 'fixed', inset: 0, zIndex: 1400, bgcolor: 'rgba(46,42,64,0.34)',
				display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
				p: '64px 20px', fontFamily: L.font, color: L.text,
			}}
		>
			<Box
				role="dialog"
				aria-label={title}
				onClick={(e) => e.stopPropagation()}
				sx={{
					width: 500, maxWidth: '100%', bgcolor: '#FFFFFF', borderRadius: '16px',
					display: 'flex', flexDirection: 'column',
					boxShadow: '0 24px 64px rgba(46,42,64,0.28)', maxHeight: 'calc(100% - 32px)',
					fontSize: '14px',
				}}
			>
				<Box sx={{
					flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
					gap: '12px', p: '18px 20px 12px',
				}}>
					<Box component="h2" sx={{ m: 0, fontSize: '20px', fontWeight: 800, letterSpacing: '-0.015em' }}>
						{title}
					</Box>
					<Box component="button" type="button" aria-label="Close" onClick={onClose} sx={xBtnSx}>
						<SvIc name="x" size={15} sw={1.8} />
					</Box>
				</Box>
				<Box component="p" sx={{ m: 0, p: '0 20px 12px', fontSize: '12.5px', fontWeight: 600, color: L.muted, lineHeight: 1.5 }}>
					Drag to set the order clients and staff see.
				</Box>
				<Box sx={{
					flex: 1, minHeight: 0, overflowY: 'auto', p: '2px 12px 8px',
					display: 'flex', flexDirection: 'column', gap: '4px',
				}}>
					{cats.map((c) => {
						const n = countFor(c.id)
						return (
							<Box
								key={c.id}
								onDragOver={(e) => {
									if (dragId && dragId !== c.id) {
										e.preventDefault()
										onReorder(dragId, c.id)
									}
								}}
								sx={{
									display: 'flex', alignItems: 'center', gap: '8px', p: '7px 8px',
									borderRadius: '11px', opacity: dragId === c.id ? 0.45 : 1,
									'&:hover': { bgcolor: '#FBFAFE' },
								}}
							>
								<Box
									component="span"
									title="Drag to reorder"
									draggable
									onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = 'move' }}
									onDragEnd={() => { setDragId(null); onDragEnd && onDragEnd() }}
									sx={{
										width: 24, height: 26, flex: 'none', display: 'flex', alignItems: 'center',
										justifyContent: 'center', color: '#C4BDD6', borderRadius: '8px', cursor: 'grab',
										'&:hover': { bgcolor: L.surface, color: L.muted },
									}}
								>
									<SvIc name="grip" size={14} />
								</Box>
								{renameId === c.id ? (
									<Box
										component="input"
										autoFocus
										defaultValue={c.name}
										onBlur={(e) => commit(c.id, e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') commit(c.id, e.target.value)
											if (e.key === 'Escape') setRenameId(null)
										}}
										sx={catNameInputSx}
									/>
								) : (
									<Box component="span" sx={{
										flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 700,
										whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
									}}>
										{c.name}
									</Box>
								)}
								<Box component="span" sx={{
									flex: 'none', display: 'inline-flex', alignItems: 'center', height: 21,
									px: '8px', borderRadius: '99px', bgcolor: L.surface, color: '#6E6886',
									fontSize: '11.5px', fontWeight: 750,
								}}>
									{n}
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 'none' }}>
									<Box component="button" type="button" title="Rename" onClick={() => setRenameId(c.id)} sx={iconBtnSx}>
										<SvIc name="pencil" size={13} />
									</Box>
									<Box
										component="button"
										type="button"
										disabled={n > 0 || saving}
										title={n > 0 ? 'Move its services first' : 'Delete category'}
										onClick={() => onDelete(c.id)}
										sx={dangerIconBtnSx}
									>
										<SvIc name="trash" size={13} />
									</Box>
								</Box>
							</Box>
						)
					})}
					{cats.length === 0 && (
						<Box sx={{ p: '14px 8px', fontSize: '13px', fontWeight: 600, color: '#B4ADC6' }}>
							No categories yet — add one below.
						</Box>
					)}
				</Box>
				<Box component="footer" sx={{
					flex: 'none', display: 'flex', alignItems: 'center', gap: '10px',
					p: '14px 20px 18px', borderTop: '1px solid #EFEBF7',
				}}>
					<Box
						component="input"
						type="text"
						autoFocus={focusAdd}
						placeholder="New category name"
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter') add() }}
						sx={{ ...plainInputSx, flex: 1, height: 40 }}
					/>
					<Box component="button" type="button" disabled={!newName.trim() || saving} onClick={add} sx={svGhostBtnSx}>
						<SvIc name="plus" size={14} sw={2} />
						<span>Add category</span>
					</Box>
					<Box component="button" type="button" onClick={onClose} sx={{ ...svPrimaryBtnSx, flex: 'none' }}>
						Done
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

/* ── mobile categories screen (svm-cats) ── */
export function SvmCatsScreen({ title, cats, countFor, saving, focusAdd, onBack, onMove, onRename, onAdd, onDelete }) {
	const [renameId, setRenameId] = useState(null)
	const [newName, setNewName] = useState('')
	const commit = (id, v) => {
		setRenameId(null)
		const name = v.trim()
		if (name) onRename(id, name)
	}
	const add = () => {
		const name = newName.trim()
		if (name) {
			onAdd(name)
			setNewName('')
		}
	}

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: L.bg, color: L.text, fontFamily: L.font, fontSize: '14px',
		}}>
			<Box component="header" sx={{
				flex: 'none', display: 'flex', alignItems: 'center', gap: '11px',
				p: '12px 16px 10px', bgcolor: L.bg, borderBottom: `1px solid ${L.border}`,
			}}>
				<Box
					component="button"
					type="button"
					aria-label="Back to services"
					onClick={onBack}
					sx={{
						width: 40, height: 40, flex: 'none', ml: '-8px', border: 'none',
						borderRadius: '11px', bgcolor: 'transparent', color: L.text,
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						cursor: 'pointer', fontFamily: 'inherit',
						'&:active': { bgcolor: L.surface },
					}}
				>
					<SvIc name="back" size={17} sw={1.8} />
				</Box>
				<Box component="strong" sx={{ flex: 1, minWidth: 0, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.015em' }}>
					{title}
				</Box>
				<Box
					component="button"
					type="button"
					onClick={onBack}
					sx={{
						display: 'inline-flex', alignItems: 'center', gap: '6px', height: 38, px: '15px',
						border: 'none', borderRadius: '11px', bgcolor: L.primary, color: L.onPrimary,
						fontSize: '13.5px', fontWeight: 700, boxShadow: '0 2px 8px rgba(124,107,217,0.28)',
						cursor: 'pointer', fontFamily: 'inherit',
						'&:active': { bgcolor: L.primaryDeep },
					}}
				>
					<SvIc name="check" size={14} sw={2.2} />
					<span>Done</span>
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '16px' }}>
				<Box component="p" sx={{ m: '0 0 12px', fontSize: '13px', fontWeight: 600, color: L.muted, lineHeight: 1.5 }}>
					Set the order clients and staff see. Categories with services can’t be deleted.
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
					{cats.map((c, i) => {
						const n = countFor(c.id)
						return (
							<Box key={c.id} sx={{
								display: 'flex', alignItems: 'center', gap: '9px', p: '11px 12px',
								bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '14px',
								boxShadow: '0 1px 3px rgba(46,42,64,0.04)',
							}}>
								{renameId === c.id ? (
									<Box
										component="input"
										autoFocus
										defaultValue={c.name}
										onBlur={(e) => commit(c.id, e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') commit(c.id, e.target.value)
											if (e.key === 'Escape') setRenameId(null)
										}}
										sx={{ ...catNameInputSx, height: 36 }}
									/>
								) : (
									<Box
										component="span"
										onClick={() => setRenameId(c.id)}
										sx={{
											flex: 1, minWidth: 0, fontSize: '14.5px', fontWeight: 700,
											whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
										}}
									>
										{c.name}
									</Box>
								)}
								<Box component="span" sx={{
									flex: 'none', display: 'inline-flex', alignItems: 'center', height: 21,
									px: '8px', borderRadius: '99px', bgcolor: L.surface, color: '#6E6886',
									fontSize: '11.5px', fontWeight: 750,
								}}>
									{n}
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 'none' }}>
									<Box component="button" type="button" disabled={i === 0 || saving} aria-label="Move up"
										onClick={() => onMove(c.id, -1)} sx={{ ...iconBtnSx, width: 34, height: 34, borderRadius: '9px' }}>
										<SvIc name="up" size={13} sw={1.9} />
									</Box>
									<Box component="button" type="button" disabled={i === cats.length - 1 || saving} aria-label="Move down"
										onClick={() => onMove(c.id, 1)} sx={{ ...iconBtnSx, width: 34, height: 34, borderRadius: '9px' }}>
										<SvIc name="down" size={13} sw={1.9} />
									</Box>
									<Box
										component="button"
										type="button"
										disabled={n > 0 || saving}
										aria-label={n > 0 ? 'Move its services first' : 'Delete category'}
										onClick={() => onDelete(c.id)}
										sx={{ ...dangerIconBtnSx, width: 34, height: 34, borderRadius: '9px' }}
									>
										<SvIc name="trash" size={13} />
									</Box>
								</Box>
							</Box>
						)
					})}
					{cats.length === 0 && (
						<Box sx={{ p: '8px 2px', fontSize: '13px', fontWeight: 600, color: '#B4ADC6' }}>
							No categories yet — add one below.
						</Box>
					)}
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mt: '14px' }}>
					<Box
						component="input"
						type="text"
						autoFocus={focusAdd}
						placeholder="New category name"
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter') add() }}
						sx={{ ...plainInputSx, flex: 1, height: 46 }}
					/>
					<Box component="button" type="button" disabled={!newName.trim() || saving} onClick={add}
						sx={{ ...svGhostBtnSx, height: 46, borderRadius: '11px' }}>
						<SvIc name="plus" size={14} sw={2} />
						<span>Add</span>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}
